import { GameObject, GameQuestion, GameMode } from '../types';
import { gameObjects, getObjectsWithMeasurement } from '../data/gameObjects';

/**
 * Dynamic Difficulty System
 * 
 * Instead of hardcoding difficulty into objects, we calculate it dynamically based on
 * the ratio between the two objects being compared:
 * 
 * Length measurements always use the longest dimension of each object to avoid ambiguity.
 * 
 * - Easy (ratio ≤ 5 AND objects ≤ thresholds): Objects are similar in size AND familiar scale
 *   Example: banana vs apple (ratio ~2.25), tennis ball vs basketball
 *   Size limits: ≤2m length, ≤50kg weight, ≤1m³ volume
 * 
 * - Medium (5 < ratio ≤ 50): Moderate size difference - medium difficulty  
 *   Example: tennis ball vs basketball (ratio ~3.6), banana vs car (ratio ~25)
 * 
 * - Hard (ratio > 50): Large size difference - hard to estimate accurately
 *   Example: banana vs blue whale (ratio ~166), apple vs swimming pool (ratio ~625)
 * 
 * This creates more natural difficulty progression and eliminates the need for
 * hardcoded difficulty values on individual objects.
 */

// Calculate dynamic difficulty based on the ratio between objects
export const calculateDynamicDifficulty = (ratio: number): 'easy' | 'medium' | 'hard' => {
  if (ratio <= 5) {
    return 'easy';     // Objects are similar in size - easy to estimate
  } else if (ratio <= 25) {
    return 'medium';   // Moderate size difference - medium difficulty (reduced from 50)
  } else {
    return 'hard';     // Large size difference - hard to estimate accurately
  }
};

// Size thresholds for easy mode - familiar, everyday objects
const EASY_MODE_THRESHOLDS = {
  length: 2.0,     // 2 meters - up to piano size
  weight: 50.0,    // 50 kg - up to moderate objects
  volume: 1.0      // 1 cubic meter - familiar-sized objects
};

export const generateQuestion = (
  measurementType?: 'length' | 'weight' | 'volume' | 'mixed',
  difficulty?: 'easy' | 'medium' | 'hard' | 'mixed',
  categories?: string[]
): GameQuestion => {
  let availableObjects = [...gameObjects];
  
  // Filter by categories if specified
  if (categories && categories.length > 0) {
    availableObjects = availableObjects.filter(obj => 
      obj.categories && obj.categories.length > 0 && obj.categories.some(cat => categories.includes(cat))
    );
  }
  
  // Determine the measurement type for this question
  let selectedMeasurementType: 'length' | 'weight' | 'volume';
  
  if (measurementType === 'mixed' || !measurementType) {
    // Randomly select a measurement type
    const measurementTypes: ('length' | 'weight' | 'volume')[] = ['length', 'weight', 'volume'];
    selectedMeasurementType = measurementTypes[Math.floor(Math.random() * measurementTypes.length)];
  } else {
    selectedMeasurementType = measurementType;
  }
  
  // Filter objects that have meaningful values for this measurement type
  let objectsWithMeasurement = availableObjects.filter(obj => {
    const value = obj.measurements[selectedMeasurementType];
    return value > 0;
  });
  
  // For easy mode, further filter by size to only include smaller, familiar objects
  if (difficulty === 'easy') {
    const threshold = EASY_MODE_THRESHOLDS[selectedMeasurementType];
    objectsWithMeasurement = objectsWithMeasurement.filter(obj => {
      const value = obj.measurements[selectedMeasurementType];
      return value <= threshold;
    });
  }
  
  // Ensure we have at least 2 objects to compare
  if (objectsWithMeasurement.length < 2) {
    // Fallback to all objects with this measurement, but maintain category filter
    let fallbackObjects = getObjectsWithMeasurement(selectedMeasurementType);
    
    // Apply category filter to fallback objects if categories are specified
    if (categories && categories.length > 0) {
      fallbackObjects = fallbackObjects.filter(obj => 
        obj.categories && obj.categories.length > 0 && obj.categories.some(cat => categories.includes(cat))
      );
    }
    
    // For easy mode, still try to apply size filter to fallback objects if possible
    if (difficulty === 'easy') {
      const threshold = EASY_MODE_THRESHOLDS[selectedMeasurementType];
      const filteredFallback = fallbackObjects.filter(obj => {
        const value = obj.measurements[selectedMeasurementType];
        return value <= threshold;
      });
      
      // Use filtered fallback if we have enough objects, otherwise use all fallback objects
      if (filteredFallback.length >= 2) {
        fallbackObjects = filteredFallback;
      }
    }
    
    if (fallbackObjects.length >= 2) {
      availableObjects = fallbackObjects;
    } else {
      // Last resort: use all objects but maintain category filter if specified
      let lastResortObjects = [...gameObjects];
      
      // Apply category filter even in last resort
      if (categories && categories.length > 0) {
        lastResortObjects = lastResortObjects.filter(obj => 
          obj.categories && obj.categories.length > 0 && obj.categories.some(cat => categories.includes(cat))
        );
      }
      
      // If we still don't have enough objects, throw an error or handle gracefully
      if (lastResortObjects.length < 2) {
        throw new Error(`Not enough objects available for the selected categories: ${categories?.join(', ')}`);
      }
      
      availableObjects = lastResortObjects;
      selectedMeasurementType = 'length'; // Default fallback
    }
  } else {
    availableObjects = objectsWithMeasurement;
  }
  
  let targetObject!: GameObject;
  let referenceObject!: GameObject;
  let calculatedDifficulty: 'easy' | 'medium' | 'hard';
  let ratio: number;
  
  // If difficulty is 'mixed', just pick any random pair
  if (difficulty === 'mixed' || !difficulty) {
    // Pick two different objects randomly
    const firstObject = availableObjects[Math.floor(Math.random() * availableObjects.length)];
    const otherObjects = availableObjects.filter(obj => obj.id !== firstObject.id);
    const secondObject = otherObjects[Math.floor(Math.random() * otherObjects.length)];
    
    // Always put the smaller object first (as target) and larger object as reference
    const firstValue = firstObject.measurements[selectedMeasurementType];
    const secondValue = secondObject.measurements[selectedMeasurementType];
    
    if (firstValue <= secondValue) {
      targetObject = firstObject;   // smaller object
      referenceObject = secondObject; // larger object
    } else {
      targetObject = secondObject;  // smaller object
      referenceObject = firstObject;  // larger object
    }
    
    // Calculate the ratio and dynamic difficulty for display purposes
    const targetValue = targetObject.measurements[selectedMeasurementType];
    const referenceValue = referenceObject.measurements[selectedMeasurementType];
    ratio = referenceValue / targetValue;
    calculatedDifficulty = calculateDynamicDifficulty(ratio);
  } else {
    // For specific difficulties, use size-based filtering approach
    const allValues = availableObjects.map(obj => obj.measurements[selectedMeasurementType]).sort((a, b) => a - b);
    
    let largerObjects: GameObject[] = [];
    let targetFound = false;
    
    if (difficulty === 'easy') {
      // Easy: ratio ≤ 5, so reference should be at most 5x larger than target
      // Pick a target from smaller objects, then find objects within 5x range
      const targetCandidates = availableObjects.filter(obj => {
        const value = obj.measurements[selectedMeasurementType];
        // Exclude extremes for easy mode
        return !obj.categories?.includes('space') && 
               !obj.categories?.includes('massive') && 
               value <= EASY_MODE_THRESHOLDS[selectedMeasurementType];
      });
      
      if (targetCandidates.length > 0) {
        targetObject = targetCandidates[Math.floor(Math.random() * targetCandidates.length)];
        const targetValue = targetObject.measurements[selectedMeasurementType];
        const maxReferenceValue = targetValue * 5;
        
        largerObjects = availableObjects.filter(obj => {
          const value = obj.measurements[selectedMeasurementType];
          return obj.id !== targetObject.id && 
                 value > targetValue && 
                 value <= maxReferenceValue &&
                 !obj.categories?.includes('space') && 
                 !obj.categories?.includes('massive');
        });
        targetFound = true;
      }
      
    } else if (difficulty === 'medium') {
      // Medium: ratio 5-25, pick from reasonable size ranges
      const moderateObjects = availableObjects.filter(obj => {
        const hasExtreme = obj.categories?.includes('space') || 
                          obj.categories?.includes('massive') || 
                          obj.categories?.includes('tiny');
        return !hasExtreme;
      });
      
      if (moderateObjects.length > 1) {
        targetObject = moderateObjects[Math.floor(Math.random() * moderateObjects.length)];
        const targetValue = targetObject.measurements[selectedMeasurementType];
        const minReferenceValue = targetValue * 5;  // At least 5x larger
        const maxReferenceValue = targetValue * 25; // At most 25x larger
        
        largerObjects = moderateObjects.filter(obj => {
          const value = obj.measurements[selectedMeasurementType];
          return obj.id !== targetObject.id && 
                 value >= minReferenceValue && 
                 value <= maxReferenceValue;
        });
        targetFound = true;
      }
      
    } else { // difficulty === 'hard'
      // Hard: ratio > 25, allow extreme comparisons
      // Pick a smaller object, then find much larger ones
      const smallerCandidates = availableObjects.filter(obj => {
        const value = obj.measurements[selectedMeasurementType];
        const percentile = allValues.findIndex(v => v >= value) / allValues.length;
        return percentile < 0.7; // Pick from smaller 70% of objects
      });
      
      if (smallerCandidates.length > 0) {
        targetObject = smallerCandidates[Math.floor(Math.random() * smallerCandidates.length)];
        const targetValue = targetObject.measurements[selectedMeasurementType];
        const minReferenceValue = targetValue * 25; // At least 25x larger
        
        largerObjects = availableObjects.filter(obj => {
          const value = obj.measurements[selectedMeasurementType];
          return obj.id !== targetObject.id && value >= minReferenceValue;
        });
        targetFound = true;
      }
    }
    
    // If we found appropriate objects, pick a reference object
    if (targetFound && largerObjects.length > 0) {
      referenceObject = largerObjects[Math.floor(Math.random() * largerObjects.length)];
      
      // Calculate the actual ratio
      const targetValue = targetObject.measurements[selectedMeasurementType];
      const referenceValue = referenceObject.measurements[selectedMeasurementType];
      ratio = referenceValue / targetValue;
      calculatedDifficulty = calculateDynamicDifficulty(ratio);
      
    } else {
      // Fallback: if no appropriate larger objects found, use random selection
      console.warn(`No appropriate ${difficulty} difficulty pairs found. Using random selection.`);
      const firstObject = availableObjects[Math.floor(Math.random() * availableObjects.length)];
      const otherObjects = availableObjects.filter(obj => obj.id !== firstObject.id);
      const secondObject = otherObjects[Math.floor(Math.random() * otherObjects.length)];
      
      const firstValue = firstObject.measurements[selectedMeasurementType];
      const secondValue = secondObject.measurements[selectedMeasurementType];
      
      if (firstValue <= secondValue) {
        targetObject = firstObject;
        referenceObject = secondObject;
      } else {
        targetObject = secondObject;
        referenceObject = firstObject;
      }
      
      const targetValue = targetObject.measurements[selectedMeasurementType];
      const referenceValue = referenceObject.measurements[selectedMeasurementType];
      ratio = referenceValue / targetValue;
      calculatedDifficulty = calculateDynamicDifficulty(ratio);
    }
    
    // Final safety check - if objects still not assigned somehow, force assignment
    if (!targetObject || !referenceObject) {
      console.warn('Objects not assigned, forcing random assignment');
      const firstObject = availableObjects[Math.floor(Math.random() * availableObjects.length)];
      const otherObjects = availableObjects.filter(obj => obj.id !== firstObject.id);
      const secondObject = otherObjects[Math.floor(Math.random() * otherObjects.length)];
      
      const firstValue = firstObject.measurements[selectedMeasurementType];
      const secondValue = secondObject.measurements[selectedMeasurementType];
      
      if (firstValue <= secondValue) {
        targetObject = firstObject;
        referenceObject = secondObject;
      } else {
        targetObject = secondObject;
        referenceObject = firstObject;
      }
      
      const targetValue = targetObject.measurements[selectedMeasurementType];
      const referenceValue = referenceObject.measurements[selectedMeasurementType];
      ratio = referenceValue / targetValue;
      calculatedDifficulty = calculateDynamicDifficulty(ratio);
    }
  }
  
  // Use appropriate precision based on the magnitude of the ratio
  let correctAnswer: number;
  if (ratio < 1) {
    // This should never happen now, but just in case
    correctAnswer = Math.max(1, Math.round(ratio * 100) / 100);
  } else if (ratio < 10) {
    // For ratios 1-10, use 2 decimal places
    correctAnswer = Math.round(ratio * 100) / 100;
  } else if (ratio < 100) {
    // For ratios 10-100, use 1 decimal place
    correctAnswer = Math.round(ratio * 10) / 10;
  } else {
    // For large ratios, round to nearest whole number
    correctAnswer = Math.round(ratio);
  }
  
  // Ensure the answer is at least 1
  if (correctAnswer < 1) {
    correctAnswer = 1;
  }
  
  return {
    id: `${targetObject.id}-${referenceObject.id}-${selectedMeasurementType}-${Date.now()}`,
    targetObject,
    referenceObject,
    correctAnswer,
    measurementType: selectedMeasurementType,
    difficulty: calculatedDifficulty, // Use calculated difficulty
    hint: generateHint(targetObject, referenceObject, correctAnswer, selectedMeasurementType)
  };
};

export const generateHint = (
  targetObject: GameObject,
  referenceObject: GameObject,
  correctAnswer: number,
  measurementType: 'length' | 'weight' | 'volume'
): string => {
  const isLarge = correctAnswer > 10;
  const isSmall = correctAnswer < 3; // Changed from < 1 since answers are now always >= 1
  
  const measurementNames = {
    length: 'length',
    weight: 'weight', 
    volume: 'volume'
  };
  
  const measurementVerbs = {
    length: 'long',
    weight: 'heavy',
    volume: 'spacious'
  };
  
  if (isSmall) {
    return `Think about it! A ${referenceObject.displayName.toLowerCase()} is only a bit more ${measurementVerbs[measurementType]} than a ${targetObject.displayName.toLowerCase()}.`;
  } else if (isLarge) {
    return `Think bigger! A ${referenceObject.displayName.toLowerCase()} is much more ${measurementVerbs[measurementType]} than a ${targetObject.displayName.toLowerCase()}.`;
  } else {
    return `Consider the ${measurementNames[measurementType]} of both objects carefully. The answer is between 3 and 10.`;
  }
};

export const calculateScore = (guess: number, correctAnswer: number): {
  points: number;
  accuracy: number;
  isCorrect: boolean;
} => {
  const accuracy = Math.max(0, 100 - Math.abs((guess - correctAnswer) / correctAnswer) * 100);
  const isCorrect = accuracy >= 80; // 80% accuracy threshold for "correct"
  
  let points = 0;
  if (accuracy >= 95) points = 100;
  else if (accuracy >= 90) points = 80;
  else if (accuracy >= 80) points = 60;
  else if (accuracy >= 70) points = 40;
  else if (accuracy >= 50) points = 20;
  else if (accuracy >= 25) points = 10;
  else points = 0; // No points for very poor guesses (< 25% accuracy)
  
  return { points, accuracy: Math.round(accuracy), isCorrect };
};

// Power-up specific scoring with order of magnitude and double points
export const calculatePowerUpScore = (
  guess: number, 
  correctAnswer: number, 
  activePowerUps?: { doublePoints?: boolean; orderOfMagnitude?: boolean }
): {
  points: number;
  accuracy: number;
  isCorrect: boolean;
  usedOrderOfMagnitude?: boolean;
  usedDoublePoints?: boolean;
} => {
  let accuracy = Math.max(0, 100 - Math.abs((guess - correctAnswer) / correctAnswer) * 100);
  let isCorrect = accuracy >= 80; // Default threshold
  let usedOrderOfMagnitude = false;
  let usedDoublePoints = false;
  
  // Check order of magnitude power-up
  if (activePowerUps?.orderOfMagnitude && !isCorrect) {
    const ratio = Math.max(guess, correctAnswer) / Math.min(guess, correctAnswer);
    if (ratio <= 10 && guess > 0 && correctAnswer > 0) {
      isCorrect = true;
      accuracy = Math.max(accuracy, 50); // Minimum 50% accuracy for order of magnitude
      usedOrderOfMagnitude = true;
    }
  }
  
  let points = 0;
  if (accuracy >= 95) points = 100;
  else if (accuracy >= 90) points = 80;
  else if (accuracy >= 80) points = 60;
  else if (accuracy >= 70) points = 40;
  else if (accuracy >= 50) points = 20;
  else if (accuracy >= 25) points = 10;
  else points = 0; // No points for very poor guesses (< 25% accuracy)
  
  // Check double points power-up (only if within 15% accuracy)
  if (activePowerUps?.doublePoints && accuracy >= 85) { // 15% error = 85% accuracy
    points *= 2;
    usedDoublePoints = true;
  }
  
  return { 
    points, 
    accuracy: Math.round(accuracy), 
    isCorrect,
    usedOrderOfMagnitude,
    usedDoublePoints
  };
};

export const formatAnswer = (answer: number): string => {
  if (answer < 0.01) {
    return answer.toExponential(2);
  } else if (answer < 1) {
    return answer.toFixed(3);
  } else if (answer < 10) {
    return answer.toFixed(2);
  } else if (answer < 100) {
    return answer.toFixed(1);
  } else {
    return Math.round(answer).toString();
  }
};

export const getAccuracyMessage = (accuracy: number): string => {
  if (accuracy >= 95) return "Perfect! 🎯";
  if (accuracy >= 90) return "Excellent! 🌟";
  if (accuracy >= 80) return "Great job! 👏";
  if (accuracy >= 70) return "Good guess! 👍";
  if (accuracy >= 50) return "Not bad! 🤔";
  if (accuracy >= 25) return "Keep trying! 💪";
  return "Way off! 😅";
};

export const getDifficultyMultiplier = (difficulty: 'easy' | 'medium' | 'hard'): number => {
  switch (difficulty) {
    case 'easy': return 1;
    case 'medium': return 1.5;
    case 'hard': return 2;
    default: return 1;
  }
};

export const generateRandomQuestion = (): GameQuestion => {
  const difficulties: ('easy' | 'medium' | 'hard' | 'mixed')[] = ['easy', 'medium', 'hard', 'mixed'];
  const randomDifficulty = difficulties[Math.floor(Math.random() * difficulties.length)];
  
  return generateQuestion('mixed', randomDifficulty);
};

export const generateQuestionForGameMode = (
  gameMode: GameMode,
  difficulty?: 'easy' | 'medium' | 'hard' | 'mixed',
  measurementType?: 'length' | 'weight' | 'volume',
  categories?: string[]
): GameQuestion => {
  switch (gameMode) {
    case 'classic':
    case 'timed':
    case 'endless':
      return generateQuestion('mixed', difficulty, categories);
    
    case 'measurement_focus':
      return generateQuestion(measurementType || 'length', difficulty, categories);
    
    case 'power_ups':
      return generateQuestion('mixed', difficulty, categories);
    
    case 'streak_master':
      return generateQuestion('mixed', difficulty, categories);
    
    default:
      return generateRandomQuestion();
  }
};

export const getMeasurementTypeDisplay = (measurementType: 'length' | 'weight' | 'volume'): {
  name: string;
  icon: string;
  unit: string;
} => {
  switch (measurementType) {
    case 'length':
      return { name: 'Length', icon: '📏', unit: 'meters' };
    case 'weight':
      return { name: 'Weight', icon: '⚖️', unit: 'kilograms' };
    case 'volume':
      return { name: 'Volume', icon: '📦', unit: 'cubic meters' };
    default:
      return { name: 'Mixed', icon: '🔀', unit: 'various' };
  }
}; 